import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { PlatformEnum, PlatformService } from '@panamax/app-state';
import { LIST_ITEM_ACTIONS } from '@usf/ngrx-list';
import { map, tap, withLatestFrom } from 'rxjs/operators';
import { selectedCustomer } from '../../../ngrx-customer/store';
import { MessageTypeEnum } from '../../../ngrx-message/constants/messageTypeEnum';
import { customerIsOgRestricted } from '../../../shared/helpers/customer.helpers';
import { LoadingSpinnerService } from '../../../shared/services/loading-spinner/loading-spinner.service';
import { ProductService } from '../../../shared/services/product/product.service';
import { ToastService } from '../../../shared/services/toast/toast.service';
import { ListsService } from '../../services/lists.service';
import { isOrderGuideItem } from '../../shared/list-detail-management/selectors/helpers/list-detail-management.selectors.helper';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class ClientListItemEffects {
  createSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ITEM_ACTIONS.createSuccess),
        map(action => {
          if (action.displaySpinner) {
            this.loadingSpinnerService.dismissSpinnerModal();
            if (!!action.addMultipleEntryPoint && !action.isNonUsf) {
              this.presentAddMultipleCreateItemSuccessToast(
                action.productQuantity,
                action.listId,
                action.listName,
                action.showAlternativeToast,
              );
            } else if (!action.addMultipleEntryPoint && !action.isNonUsf) {
              this.presentCreateItemSuccessToast(
                action.productQuantity,
                action.listId,
                action.listName,
              );
            } else if (action.isNonUsf) {
              this.presentNonUsfCreateItemSuccessToast(action.listId);
            }
          }
        }),
      ),
    { dispatch: false },
  );

  createFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ITEM_ACTIONS.createFail),
        tap(action => {
          if (action.isNonUsf) {
            this.loadingSpinnerService.dismissSpinnerModal();
            this.toastService.presentToastMsg(
              this.translateService.instant(
                'i18n.lists.addToListModal.thereWasAnError',
              ),
              'red-toast',
              MessageTypeEnum.error,
              [],
            );
          } else {
            this.presentGenericErrorToast();
          }
        }),
      ),
    { dispatch: false },
  );

  copySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ITEM_ACTIONS.copySuccess),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal();
          let display;
          if (
            action.numberOfItemsCopied > 1 &&
            action.numberOfGroupsCopiedTo > 1
          ) {
            display = this.translateService.instant(
              'i18n.lists.copyItemsGroupsSuccess',
              {
                numberOfProducts: action.numberOfItemsCopied,
                numberOfGroupsCopiedTo: action.numberOfGroupsCopiedTo,
              },
            );
          } else if (
            action.numberOfItemsCopied > 1 &&
            action.numberOfGroupsCopiedTo === 1
          ) {
            display = this.translateService.instant(
              'i18n.lists.copyItemsGroupSuccess',
              { numberOfProducts: action.numberOfItemsCopied },
            );
          } else if (
            action.numberOfItemsCopied === 1 &&
            action.numberOfGroupsCopiedTo > 1
          ) {
            display = this.translateService.instant(
              'i18n.lists.copyItemGroupsSuccess',
              {
                numberOfGroupsCopiedTo: action.numberOfGroupsCopiedTo,
              },
            );
          } else {
            display = this.translateService.instant(
              'i18n.lists.copyItemGroupSuccess',
            );
          }
          this.toastService.presentToastMsg(
            display,
            'green-toast',
            MessageTypeEnum.success,
            [],
          );
        }),
      ),
    { dispatch: false },
  );

  copyFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ITEM_ACTIONS.copyFail),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal();
          this.toastService.presentToastMsg(
            this.translateService.instant(
              'i18n.lists.copyProductsModal.failureGetData',
            ),
            'red-toast',
            MessageTypeEnum.error,
            [],
          );
        }),
      ),
    { dispatch: false },
  );

  deleteSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ITEM_ACTIONS.deleteSuccess),
        tap(action => {
          if (action.displaySpinner) {
            this.loadingSpinnerService.dismissSpinnerModal();
            this.toastService.presentToastMsg(
              this.translateService.instant('i18n.lists.deleteProductsSuccess'),
              'green-toast',
              MessageTypeEnum.success,
              [],
            );
          }
        }),
      ),
    { dispatch: false },
  );

  loadSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ITEM_ACTIONS.loadSuccess),
        withLatestFrom(this.store.select(selectedCustomer)),
        map(([action, selectedCustomer]) => {
          if (customerIsOgRestricted(selectedCustomer)) {
            const productNumbers: number[] = [];
            // Collect OG item product numbers and make products call
            for (const listItem of action.listItems) {
              if (isOrderGuideItem(listItem)) {
                if (!productNumbers.includes(listItem?.productNumber)) {
                  productNumbers.push(listItem.productNumber);
                }
              }
            }
            this.productService.loadProducts(productNumbers);
          }
        }),
      ),
    { dispatch: false },
  );

  loadFromStorageSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ITEM_ACTIONS.loadFromStorageSuccess),
        withLatestFrom(this.store.select(selectedCustomer)),
        map(([action, selectedCustomer]) => {
          if (
            customerIsOgRestricted(selectedCustomer) &&
            action.listItems.ids
          ) {
            const productNumbers: number[] = [];
            // Collect OG item product numbers and make products call
            for (const listItemId of action.listItems.ids) {
              const listItem = action.listItems.entities[listItemId];
              if (isOrderGuideItem(listItem)) {
                if (!productNumbers.includes(listItem?.productNumber)) {
                  productNumbers.push(listItem.productNumber);
                }
              }
            }
            this.productService.loadProducts(productNumbers);
          }
        }),
      ),
    { dispatch: false },
  );

  replaceProductSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(LIST_ITEM_ACTIONS.replaceProductSuccess),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal();
          this.toastService.presentToastMsg(
            this.translateService.instant('i18n.lists.replaceProductSuccess', {
              originalNum: action.originalNum,
              replacementNum: action.replacementNum,
            }),
            'green-toast',
            MessageTypeEnum.success,
            [],
          );
        }),
      ),
    { dispatch: false },
  );

  domainCallFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          LIST_ITEM_ACTIONS.deleteFail,
          LIST_ITEM_ACTIONS.replaceProductFail,
          LIST_ITEM_ACTIONS.createNoteFail,
          LIST_ITEM_ACTIONS.updateNoteFail,
          LIST_ITEM_ACTIONS.deleteNoteFail,
        ),
        tap(action => {
          this.presentGenericErrorToast();
        }),
      ),
    { dispatch: false },
  );

  setupLoadingSpinnerForNotes$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          LIST_ITEM_ACTIONS.createNote,
          LIST_ITEM_ACTIONS.updateNote,
          LIST_ITEM_ACTIONS.deleteNote,
        ),
        tap(action => {
          this.loadingSpinnerService.createSpinnerModal();
        }),
      ),
    { dispatch: false },
  );

  dismissLoadingSpinnerForNotesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          LIST_ITEM_ACTIONS.createNoteSuccess,
          LIST_ITEM_ACTIONS.updateNoteSuccess,
          LIST_ITEM_ACTIONS.deleteNoteSuccess,
        ),
        tap(action => {
          this.loadingSpinnerService.dismissSpinnerModal();
        }),
      ),
    { dispatch: false },
  );

  presentAddMultipleCreateItemSuccessToast(
    productQuantity: number,
    listId: number,
    listName: string,
    showAlternativeToast: boolean = false,
  ) {
    let display = '';
    let buttons = [];
    if (showAlternativeToast) {
      if (productQuantity === 1) {
        display = this.translateService.instant(
          'i18n.lists.addProductsAlternativeSuccessManagementSingular',
        );
      } else {
        display = this.translateService.instant(
          'i18n.lists.addProductsAlternativeSuccessManagementPlural',
          {
            productQuantity: productQuantity,
          },
        );
      }
    } else {
      if (productQuantity === 1) {
        display = this.translateService.instant(
          'i18n.lists.addProductsAlternativeSuccessLandingSingular',
          {
            listName: listName,
          },
        );
      } else {
        display = this.translateService.instant(
          'i18n.lists.addProductsAlternativeSuccessLandingPlural',
          {
            productQuantity: productQuantity,
            listName: listName,
          },
        );
      }
    }
    buttons.push(
      {
        text: this.translateService.instant('i18n.lists.goToList'),
        side: 'end',
        handler: () => {
          this.listsService.goToShoppingList([
            'lists',
            'management',
            'SL-' + listId,
          ]);
        },
      },
      {
        icon: 'close-circle',
        role: 'cancel',
      },
    );
    this.toastService.presentToastMsg(
      display,
      'green-toast',
      MessageTypeEnum.success,
      buttons,
      null,
    );
  }

  presentCreateItemSuccessToast(
    productQuantity: number,
    listId: number,
    listName: string,
  ) {
    let display = '';
    let buttons = [];
    if (this.platformService.platformType === PlatformEnum.mobile) {
      display = this.translateService.instant(
        'i18n.lists.addProductsSuccessTouch',
      );
    } else {
      display =
        productQuantity === 1
          ? this.translateService.instant('i18n.lists.addProductSuccess', {
              listName: listName,
            })
          : this.translateService.instant('i18n.lists.addProductsSuccess', {
              productQuantity: productQuantity,
              listName: listName,
            });
    }
    buttons.push({
      text: this.translateService.instant('i18n.lists.goToList'),
      side: 'end',
      handler: () => {
        this.listsService.goToShoppingList([
          'lists',
          'management',
          'SL-' + listId,
        ]);
      },
    });
    this.toastService.presentToastMsg(
      display,
      'green-toast',
      MessageTypeEnum.success,
      buttons,
    );
  }

  presentNonUsfCreateItemSuccessToast(listId: number) {
    let display = '';
    const buttons = [];
    display = this.translateService.instant(
      'i18n.lists.addToListModal.yourNonUsfProducts',
    );
    if (!this.router.url.includes('SL-' + listId)) {
      buttons.push({
        text: this.translateService.instant(
          'i18n.lists.massDeleteModal.successToastButton',
        ),
        side: 'end',
        handler: () => {
          this.listsService.goToShoppingList([
            'lists',
            'management',
            'SL-' + listId,
          ]);
        },
      });
    }
    this.toastService.presentToastMsg(
      display,
      'green-toast',
      MessageTypeEnum.success,
      buttons,
    );
  }

  presentGenericErrorToast() {
    this.loadingSpinnerService.dismissSpinnerModal();
    this.toastService.presentToastMsg(
      this.translateService.instant('i18n.lists.genericError'),
      'red-toast',
      MessageTypeEnum.error,
      [],
    );
  }

  constructor(
    private actions$: Actions,
    private loadingSpinnerService: LoadingSpinnerService,
    private platformService: PlatformService,
    private translateService: TranslateService,
    private listsService: ListsService,
    private toastService: ToastService,
    private productService: ProductService,
    private store: Store,
    private router: Router,
  ) {}
}
